/* eslint-disable no-unused-vars */
import {
  Button,
  Col,
  Row,
  Select,
  Input,
  Checkbox,
  message,
  Spin,
  Alert,
} from "antd";
import React, { useState } from "react";
import "../../styles/compareWizard.css";
import FileDropzone from "../generic/FileDropzone";
import StepFooter from "./StepFooter";
import { getQueryHeaders } from "../../service/db.service";
import {
  getColumnFromSuite,
  getLinkedSql,
  getTestSuiteByType,
} from "../../service/suite.service";

const { TextArea } = Input;
const { Option } = Select;

// ─── Column name validation ───────────────────────────────────
// These are common PostgreSQL expressions returned as column names
// when the user forgets to alias them. They're invalid for mapping.
const INVALID_COLUMN_PATTERNS = [
  /^coalesce$/i,
  /^count$/i,
  /^sum$/i,
  /^avg$/i,
  /^max$/i,
  /^min$/i,
  /^case$/i,
  /^nullif$/i,
  /^concat$/i,
  /^trim$/i,
  /^lower$/i,
  /^upper$/i,
  /^cast$/i,
  /^\?column\?$/i,
  /^row_number$/i,
  /^rank$/i,
  /^dense_rank$/i,
];

function isInvalidColumnName(name) {
  if (!name || name.trim() === "") return true;
  return INVALID_COLUMN_PATTERNS.some((pattern) => pattern.test(name.trim()));
}

function SelectDB(props) {
  const type = props.compareType;
  const index = type === "db" ? 2 : 3;
  const previousVisible = type === "db" ? false : true;
  const [sqlSelection, setSqlSelection] = useState();
  const [dbConnection, setDbConnection] = useState();
  const [testSuiteOptions, setTestSuiteOptions] = useState([]);
  const [testSuite, setTestSuite] = useState("");
  const [sqlContent, setSqlContent] = useState();
  const [file, setFile] = useState();
  const [sqlHeader, setSqlHeader] = useState([]);
  const [inputBackground, setInputBackground] = useState("grey");
  const [fileUploadCheck, setFileUploadCheck] = useState(false);
  const [testSuiteLoading, setTestSuiteLoading] = useState(true);
  const [selectedDatabaseList, setSelectedDatabaseList] = useState([]);
  const [invalidColumns, setInvalidColumns] = useState([]); // tracks bad column names

  const sqlOptions = [
    { value: "testSuite", label: "From Test Suite" },
    { value: "inputSQL", label: "Enter/Upload SQL" },
    { value: "queryBuilder", label: "Query Builder" },
  ];

  const onClickNext = () => {
    if (dbConnection != null && sqlSelection != null) {
      if (sqlSelection === "inputSQL") {
        getSqlHeader(dbConnection, sqlContent, false);
      } else if (sqlSelection === "testSuite") {
        getTestSuiteHeader();
      }
    } else {
      message.error("Select all the mandatory options");
    }
  };

  const onClickBack = () => {
    props.clickPrevious(1);
  };

  const onChangeSql = (value) => {
    setSqlSelection(value);
    setInvalidColumns([]); // clear any previous warnings
    if (value === "testSuite") getTestSuite();
  };

  const onChangeTestSuite = (value) => {
    setTestSuite(value);
  };

  const onChangeEnvironment = (value) => {
    const dbForSelectedEnvironment = props.dbConnections.filter(
      (item) => item.environmentName === value,
    );
    setSelectedDatabaseList(dbForSelectedEnvironment);
  };

  const getSqlHeader = (dbConnection, sqlContent, isSuite) => {
    getQueryHeaders(dbConnection, sqlContent)
      .then((res) => {
        if (res.status === 500) {
          message.error(
            isSuite
              ? "Please check if the Sql in the Selected Test Suite is valid"
              : "Please check if the Input sql is valid!",
          );
        } else {
          const headers = res.data;

          // ─── Validate column names ──────────────────────────
          const badColumns = headers.filter((col) => isInvalidColumnName(col));

          if (badColumns.length > 0) {
            // Show warning but DO NOT block — user can still proceed
            // They need to add AS aliases to the SQL for these columns
            setInvalidColumns(badColumns);
            message.warning(
              `${badColumns.length} column(s) have invalid names. Add an alias (e.g. AS column_name) to use them in mapping.`,
              6,
            );
            // Still set headers and proceed — user may not need those columns
          } else {
            setInvalidColumns([]);
          }

          setSqlHeader(headers);
          props.clickNext(index, headers, dbConnection, sqlContent, testSuite);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 500) {
          message.error(
            isSuite
              ? "Please check if the Sql in the Selected Test Suite is valid"
              : "Please check if the Input sql is valid!",
          );
        } else {
          message.error("An unexpected error occurred.");
        }
      });
  };

  const getTestSuiteHeader = () => {
    getColumnFromSuite(testSuite, "Field Level").then((res) => {
      if (res.status === 500) {
        message.error(`Check the test cases in test Suite ${testSuite}`);
      } else {
        getLinkedSql(testSuite).then((suiteResponse) => {
          if (res.status === 500) {
            message.error(`Check the test cases in test Suite ${testSuite}`);
          } else {
            setSqlHeader(res.data);
            let linkedSql = suiteResponse.data;
            getSqlHeader(dbConnection, linkedSql, true);
          }
        });
      }
    });
  };

  const getContent = (fileObject) => {
    if (fileObject) {
      const reader = new FileReader();
      reader.onload = (e) => setSqlContent(e.target.result);
      reader.onerror = (e) =>
        console.error("Error reading file:", e.target.error);
      reader.readAsText(fileObject);
    }
  };

  const getTestSuite = () => {
    getTestSuiteByType("Database").then((res) => {
      setTestSuiteOptions(res.data);
      setTestSuiteLoading(false);
    });
  };

  return (
    <div className="cmp-pane">
      <div className="cmp-field">
        <span className="cmp-field-label">Select environment</span>
        <Select
          showSearch
          placeholder="select environment"
          optionFilterProp="label"
          style={{ width: "100%" }}
          onChange={(e) => onChangeEnvironment(e)}
        >
          {props.environments.map((data) => (
            <Option key={data} value={data}>
              {data}
            </Option>
          ))}
        </Select>
      </div>

      <div className="cmp-field">
        <span className="cmp-field-label">Select database</span>
        <Select
          showSearch
          placeholder="select database"
          optionFilterProp="label"
          style={{ width: "100%" }}
          onChange={(e) => setDbConnection(e)}
        >
          {selectedDatabaseList.map((data) => (
            <Option key={data.displayName} value={data.displayName}>
              {data.displayName}
            </Option>
          ))}
        </Select>
      </div>

      <div className="cmp-field">
        <span className="cmp-field-label">Select input SQL</span>
        <Select
          showSearch
          placeholder="select sql option"
          optionFilterProp="label"
          style={{ width: "100%" }}
          options={sqlOptions}
          onChange={onChangeSql}
        />
      </div>

      {(() => {
        switch (sqlSelection) {
          case "testSuite":
            return (
              <div className="cmp-field">
                <span className="cmp-field-label">Select test suite</span>
                <Select
                  showSearch
                  placeholder="select test suite"
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  onChange={onChangeTestSuite}
                >
                  {testSuiteLoading ? (
                    <Spin />
                  ) : (
                    testSuiteOptions.map((option) => (
                      <Option
                        key={option.testSuiteId}
                        value={option.testSuiteId}
                      >
                        {option.testSuiteName}
                      </Option>
                    ))
                  )}
                </Select>
              </div>
            );

          case "inputSQL":
            return (
              <>
                <div className="cmp-field">
                  <span className="cmp-field-label">Upload a file</span>
                  <Checkbox
                    checked={fileUploadCheck}
                    onChange={() => {
                      setInputBackground("white");
                      setFileUploadCheck(!fileUploadCheck);
                    }}
                  />
                </div>
                {!fileUploadCheck ? (
                  <div className="cmp-block">
                    <span className="cmp-field-label">Enter SQL script</span>
                    <TextArea
                      rows={5}
                      style={{ width: "100%" }}
                      onChange={(e) => {
                        setSqlContent(e.target.value);
                        setInvalidColumns([]);
                      }}
                    />
                  </div>
                ) : (
                  <div className="cmp-block">
                    <span className="cmp-field-label">
                      Upload file (.txt or .sql)
                    </span>
                    <FileDropzone
                      onFile={(f) => {
                        setFile(f);
                        getContent(f);
                      }}
                      accept=".txt,.sql"
                      hint="Plain-text SQL file (.txt or .sql)"
                    />
                  </div>
                )}
              </>
            );

          case "queryBuilder":
            return (
              <p style={{ color: "var(--arc-table)" }}>
                This feature is under construction. Watch this space.
              </p>
            );

          default:
            return <span />;
        }
      })()}

      {invalidColumns.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Alert
            type="warning"
            showIcon
            message="Some columns have invalid names for mapping"
            description={
              <div>
                <p style={{ marginBottom: 6 }}>
                  The following columns are unnamed expressions. Add an{" "}
                  <code>AS</code> alias in your SQL to use them in mapping:
                </p>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {invalidColumns.map((col) => (
                    <li key={col}>
                      <code>{col}</code> → e.g.{" "}
                      <code>... AS {col.toLowerCase()}_value</code>
                    </li>
                  ))}
                </ul>
                <p style={{ marginTop: 6, marginBottom: 0 }}>
                  You can still proceed — just avoid mapping these columns.
                </p>
              </div>
            }
          />
        </div>
      )}

      <StepFooter
        showBack={previousVisible}
        onBack={onClickBack}
        onNext={onClickNext}
      />
    </div>
  );
}

export default SelectDB;
